import { useState, useMemo } from 'react';
import { DataTypeType } from 'modules/domains';
import { isNumber } from 'utils/tsHelpers';
import { BaseOption } from 'ui/selects';
import { useFetchTypeAheads } from './useFetchTypeAheads';
import { TypeAhead } from '../../../../domain';

export const getBaseOptionsFromTypeAheads = (typeAheads: TypeAhead[]): BaseOption<string>[] =>
  typeAheads.map(({ term }) => ({
    id: term,
    label: term,
    value: term,
  }));

const getDefaultValuesFromOptions = <T>(defaultValue: T, options: BaseOption<string>[]) =>
  options
    .filter(({ value }) => {
      if (isNumber(defaultValue)) return value === defaultValue.toString();
      else return value === defaultValue;
    })
    .map(({ value }) => value);

/** creates type ahead state for number and string inputs */
export const useTypeAheadState = <T>(
  fieldName: string,
  defaultValue: T,
  dataType?: DataTypeType,
) => {
  const { data: typeAheads = [], isLoading } = useFetchTypeAheads(fieldName, dataType);
  const [value, setValue] = useState<T>(defaultValue);
  const [isEditing, setEditing] = useState<boolean>(false);
  const turnOnEditing = () => setEditing(true);
  const turnOffEditing = () => setEditing(false);

  const options = useMemo(() => getBaseOptionsFromTypeAheads(typeAheads), [typeAheads, isLoading]);
  const defaultValues = useMemo(
    () => getDefaultValuesFromOptions(defaultValue, options),
    [defaultValue, options],
  );

  return {
    value,
    setValue,
    isEditing,
    turnOnEditing,
    turnOffEditing,
    options,
    defaultValues,
    isLoading,
  };
};
